import { create } from 'zustand';
import type {
  Feature,
  Filter,
  FilterForApiCall,
  FiltersDictionaryForPrompter,
  NonNumericMetadata,
  NumericMetadata,
  SubFilter,
} from '../interfaces/filter';
import type { Artwork } from '../interfaces/artwork';
import type { SortBy } from '../interfaces/sort-by';
import type { Tags } from '../interfaces/prompter-result';
import { ModalType } from '../interfaces/modal-types.enum';
import { useModalStore } from './modal-store';
import { generateRandomString } from '../shared/utils/random';
import { debounce } from '../shared/utils/debounce';

// Port of src/app/services/filters-state.service.ts.
//
// IDENTITY / CLONING STRATEGY — version counter (NOT clone-on-write for the tree):
// The Angular service mutates the `Filter[]` tree in place everywhere
// (subFilter.state, filter.totalActiveFilters, ...) and — critically —
// `selectedFilters[title].filters` holds LIVE SubFilter references into the main
// tree (see addOrUpdateSubFilter: the Filter shell is deep-cloned but its
// `filters` array is rebuilt from tree SubFilter references). Deep-cloning on
// every change would sever that identity sharing and break removeFilter /
// handleGenreSubgenreLogic / constructFeaturesAndCheckIfNotEmpty semantics.
// We therefore keep `filters`, `selectedFilters`, `filterForApiCall`,
// `filtersDictionaryForPrompter` and `tagsWithOperator` as mutable working
// structures and bump `filtersVersion` on every mutation — this mirrors the
// Angular pattern of `this.filters.set(currentFilters)` /
// `setRandomUpdateSearchPayloadSignal()` used purely as change notifications.
// Components that need to re-render on tree changes subscribe to
// `filtersVersion` (plus the tree reference itself).
//
// Immutable-by-replacement fields (plain values / arrays no consumer mutates):
// lockedFilters, sortByArray (changeSelectedSort clones), selectedArtwork,
// prompterText, searchPayload and the scalar search params.

/**
 * Port of the Angular `searchPayloadSignal` computed.
 *
 * Consumers (verified by grepping `searchPayloadSignal` across src/app *.ts):
 * the ONLY consumer is the service's own constructor `effect()` that debounces
 * 300ms and regenerates the prompter text (nav-bar docs reference it, but no
 * component reads it). It is kept as a state field recomputed whenever any of
 * its inputs change so external code may still subscribe to it.
 */
export interface SearchPayload {
  updateFilterSignal: string;
  lyrics: string | undefined;
  artist: string | undefined;
  searchArtist: string | undefined;
  narrative: string | undefined;
}

export interface FiltersState {
  // --- signal-backed state ---
  lockedFilters: string[];
  filtersVisible: boolean;
  lyricsContains: string | undefined;
  artistSearchStr: string | undefined;
  searchOnThisArtist: string | undefined;
  narrative: string | undefined;
  multipleArtworksIds: string | undefined;
  /** Angular: private `_updateSearchPayloadSignal` exposed via getter. */
  updateSearchPayloadSignal: string;
  /** Angular: `searchPayloadSignal` computed — see SearchPayload docs above. */
  searchPayload: SearchPayload;
  /** Angular: `prompterText$` BehaviorSubject<string | undefined>. */
  prompterText: string | undefined;
  selectedArtwork: Artwork | undefined;
  sortByArray: SortBy[];
  youtubeIdFromUrl: string | undefined;
  originalFilters: Filter[];
  filters: Filter[];
  /** Angular: `metadataFilters` computed — recomputed whenever `filters` is replaced. */
  metadataFilters: Filter[];
  /** Bumped on every in-place mutation of the filters tree / selectedFilters. */
  filtersVersion: number;

  // --- plain class fields in Angular (mutable working structures) ---
  selectedFilters: { [title: string]: Filter };
  filterForApiCall: FilterForApiCall;
  filtersDictionaryForPrompter: FiltersDictionaryForPrompter;
  tagsWithOperator: Tags[];

  // --- setters for signals Angular exposed publicly (callers did `signal.set(...)`) ---
  setLockedFilters: (lockedFilters: string[]) => void;
  setFiltersVisible: (visible: boolean) => void;
  setLyricsContains: (lyricsContains: string | undefined) => void;
  setArtistSearchStr: (artistSearchStr: string | undefined) => void;
  setSearchOnThisArtist: (searchOnThisArtist: string | undefined) => void;
  setNarrative: (narrative: string | undefined) => void;
  setMultipleArtworksIds: (multipleArtworksIds: string | undefined) => void;
  setSelectedArtwork: (artwork: Artwork | undefined) => void;
  setSortByArray: (sortByArray: SortBy[]) => void;
  setYoutubeIdFromUrl: (youtubeId: string | undefined) => void;
  /** Angular: `prompterText$.next(...)` (nav-bar pushes short prompts / undefined). */
  setPrompterText: (prompterText: string | undefined) => void;
  setTagsWithOperator: (tags: Tags[]) => void;
  setRandomUpdateSearchPayloadSignal: () => void;

  // --- actions (Angular public methods) ---
  setFilters: (filters: Filter[]) => void;
  resetFilters: () => void;
  getSortById: (id: number) => SortBy;
  resetSortBy: () => void;
  changeSelectedSort: (sortBy: SortBy) => void;
  getSelectedSortAddress: () => string;
  getSelectedSortBy: () => SortBy | undefined;
  changeSelectedArtwork: (artwork: Artwork | undefined) => void;
  updateFilter: (updatedFilter: Filter) => void;
  addOrChangeSubFilterSelectedFilters: (
    filter: Filter,
    subFilter: SubFilter,
    state: 'green' | 'red',
    isInputFilter?: boolean,
    genresOperator?: 'and' | 'or'
  ) => void;
  removeFilter: (filter: Filter, subFilter: SubFilter) => void;
  clearSubFiltersFromFilter: (filter: Filter) => void;
  getPrimaryFilterId: () => number | null;
  resetSelectedFilters: () => void;
  getSelectedSubFilters: (title: string) => { id: number; state: 'green' | 'red' }[];
  reapplySubFilters: (title: string, entries: { id: number; state: 'green' | 'red' }[]) => void;
  constructFeaturesAndCheckIfNotEmpty: () => boolean;
  applyFilterFromUrl: (
    filterArray: { id: number; state: 'green' | 'red' }[],
    primaryFilterId?: number,
    lyricsContains?: string,
    searchInArtist?: string,
    multipleArtworksIds?: string,
    searchOnThisArtist?: string,
    narrative?: string
  ) => void;
  convertFiltersToQueryString: () => string;
  areFiltersEmpty: () => boolean;
  resetParams: () => void;
}

/** Angular: `initializeSortBy()` (public method, also used for the signal's initial value). */
export function initializeSortBy(): SortBy[] {
  return [
    {
      name: 'Weighted Similarity',
      address: 'search',
      selected: true,
      id: 0,
    },
    {
      name: 'Production & Sound',
      address: 'sound-alike-search',
      selected: false,
      id: 1,
    },
    {
      name: 'Theme & Mood',
      address: 'semantics-alike-search',
      selected: false,
      id: 2,
    },
    {
      name: 'Writing Style',
      address: 'aesthetics-alike-search',
      selected: false,
      id: 3,
    },
    {
      name: 'Music & Arrangement',
      address: 'composition-alike-search',
      selected: false,
      id: 4,
    },
  ];
}

function resetFiltersForApiCall(): FilterForApiCall {
  return { features: [], numericMetadata: [], nonNumericMetadata: {} };
}

/**
 * Computes the next `searchPayload` from the given state slice. Must be invoked
 * by every action that changes one of the payload inputs (mirrors the Angular
 * `searchPayloadSignal` computed re-evaluating).
 */
function computeSearchPayload(state: Pick<FiltersState,
  'updateSearchPayloadSignal' | 'lyricsContains' | 'artistSearchStr' | 'searchOnThisArtist' | 'narrative'>): SearchPayload {
  return {
    updateFilterSignal: state.updateSearchPayloadSignal,
    lyrics: state.lyricsContains,
    artist: state.artistSearchStr,
    searchArtist: state.searchOnThisArtist,
    narrative: state.narrative,
  };
}

export const useFiltersStore = create<FiltersState>()((set, get) => ({
  lockedFilters: [],
  filtersVisible: false,
  lyricsContains: undefined,
  artistSearchStr: undefined,
  searchOnThisArtist: undefined,
  narrative: undefined,
  multipleArtworksIds: undefined,
  updateSearchPayloadSignal: generateRandomString(),
  searchPayload: {
    updateFilterSignal: '',
    lyrics: undefined,
    artist: undefined,
    searchArtist: undefined,
    narrative: undefined,
  },
  prompterText: undefined,
  selectedArtwork: undefined,
  sortByArray: initializeSortBy(),
  youtubeIdFromUrl: undefined,
  originalFilters: [],
  filters: [],
  metadataFilters: [],
  filtersVersion: 0,

  selectedFilters: {},
  filterForApiCall: resetFiltersForApiCall(),
  filtersDictionaryForPrompter: {},
  tagsWithOperator: [],

  setLockedFilters: (lockedFilters) => set({ lockedFilters }),
  setFiltersVisible: (filtersVisible) => set({ filtersVisible }),

  // The four payload-input setters below mirror the Angular constructor effect:
  // setting the signal re-evaluates searchPayloadSignal and (after a 300ms
  // debounce) regenerates the prompter text.
  setLyricsContains: (lyricsContains) => {
    set((state) => ({ lyricsContains, searchPayload: computeSearchPayload({ ...state, lyricsContains }) }));
    schedulePrompterRegen();
  },
  setArtistSearchStr: (artistSearchStr) => {
    set((state) => ({ artistSearchStr, searchPayload: computeSearchPayload({ ...state, artistSearchStr }) }));
    schedulePrompterRegen();
  },
  setSearchOnThisArtist: (searchOnThisArtist) => {
    set((state) => ({ searchOnThisArtist, searchPayload: computeSearchPayload({ ...state, searchOnThisArtist }) }));
    schedulePrompterRegen();
  },
  setNarrative: (narrative) => {
    set((state) => ({ narrative, searchPayload: computeSearchPayload({ ...state, narrative }) }));
    schedulePrompterRegen();
  },
  // NOT part of searchPayloadSignal in Angular — no payload recompute, no debounce.
  setMultipleArtworksIds: (multipleArtworksIds) => set({ multipleArtworksIds }),
  setSelectedArtwork: (selectedArtwork) => set({ selectedArtwork }),
  setSortByArray: (sortByArray) => set({ sortByArray }),
  setYoutubeIdFromUrl: (youtubeIdFromUrl) => set({ youtubeIdFromUrl }),
  setPrompterText: (prompterText) => set({ prompterText }),
  setTagsWithOperator: (tagsWithOperator) => set({ tagsWithOperator }),

  setRandomUpdateSearchPayloadSignal: () => {
    set((state) => {
      const updateSearchPayloadSignal = generateRandomString();
      return {
        updateSearchPayloadSignal,
        searchPayload: computeSearchPayload({ ...state, updateSearchPayloadSignal }),
      };
    });
    schedulePrompterRegen();
  },

  setFilters: (filters) => {
    const partial: Partial<FiltersState> = {};

    // Save the filters as a backup in originalFilters
    if (get().originalFilters.length === 0) {
      partial.originalFilters = JSON.parse(JSON.stringify(filters));
    }

    const filtersDictionaryForPrompter: FiltersDictionaryForPrompter = {};
    filters.forEach((filter) => {
      filter.filters.forEach((subFilter) => {
        filtersDictionaryForPrompter[subFilter.id] = {
          name: subFilter.name,
          familyName: filter.title,
        };
      });
    });
    partial.filtersDictionaryForPrompter = filtersDictionaryForPrompter;

    // Update lockedFilters with only a subset of filters (e.g., first 6 elements)
    partial.lockedFilters = filters
      .map((filter, index) => (index > 6 ? filter.title : null))
      .filter(Boolean) as string[];

    partial.filters = filters;
    partial.metadataFilters = filters.filter((f) => f.isMetadata);

    set((state) => ({ ...partial, filtersVersion: state.filtersVersion + 1 }));
  },

  resetFilters: () => {
    get().setLyricsContains(undefined);
    get().setRandomUpdateSearchPayloadSignal();

    // Restore the original filters from the backup
    const filters: Filter[] = JSON.parse(JSON.stringify(get().originalFilters));
    set((state) => ({
      filters,
      metadataFilters: filters.filter((f) => f.isMetadata),
      filtersVersion: state.filtersVersion + 1,
    }));
  },

  getSortById: (id) => get().sortByArray.find((s) => s.id === id)!,

  resetSortBy: () => set({ sortByArray: initializeSortBy() }),

  changeSelectedSort: (sortBy) => {
    // Clone-on-write (Angular mutated the array in place and re-set the signal).
    const sortByArray = get().sortByArray.map((sort) => ({
      ...sort,
      selected: sort.name === sortBy.name,
    }));
    set({ sortByArray });
    generatePrompterText(set, get);
  },

  getSelectedSortAddress: () => {
    const selectedSort = get().getSelectedSortBy();
    return selectedSort ? selectedSort.address : '';
  },

  getSelectedSortBy: () => get().sortByArray.find((sort) => sort.selected),

  changeSelectedArtwork: (artwork) => {
    set({ selectedArtwork: artwork });
    generatePrompterText(set, get);
  },

  updateFilter: (updatedFilter) => {
    const currentFilters = get().filters;
    const index = currentFilters.findIndex((filter) => filter.title === updatedFilter.title);
    if (index !== -1) {
      currentFilters[index] = updatedFilter;
      set((state) => ({ filtersVersion: state.filtersVersion + 1 }));
    }
  },

  addOrChangeSubFilterSelectedFilters: (filter, subFilter, state, isInputFilter = false, genresOperator) => {
    const prevState = subFilter.state;
    if (filter.title === 'Genre' || filter.title === 'Sub-genre') {
      handleGenreSubgenreLogic(get, filter, subFilter);
    }

    if (isInputFilter || isSingleSelection(filter.title)) {
      ensureSingleSelectionActive(get, filter, subFilter);
    }

    subFilter.state = state;
    subFilter.genresOperator = genresOperator;

    const selectedFilter = get().selectedFilters[filter.title];
    addOrUpdateSubFilter(get, selectedFilter, filter, subFilter, prevState);

    get().updateFilter(filter); // Update the filter after making changes
    checkFilterOverload(get);
    generatePrompterText(set, get);
  },

  removeFilter: (filter, subFilter) => {
    subFilter.state = 'none';

    const selectedFilter = get().selectedFilters[filter.title];
    removeSubFilterIfNecessary(get, selectedFilter, filter, subFilter);

    get().updateFilter(filter); // Update the filter after making changes
    checkFilterOverload(get);
    generatePrompterText(set, get);
  },

  clearSubFiltersFromFilter: (filter) => {
    filter.filters.forEach((subFilter) => {
      subFilter.state = 'none';
    });
    filter.totalActiveFilters = 0;
    if (Object.prototype.hasOwnProperty.call(get().selectedFilters, filter.title)) {
      delete get().selectedFilters[filter.title];
    }

    if (!get().getPrimaryFilterId()) {
      delete get().filterForApiCall.primaryFilterId;
    }

    get().updateFilter(filter);
    generatePrompterText(set, get);
  },

  getPrimaryFilterId: () => {
    // Iterate over the values of selectedFilters to find the first green sub-filter
    for (const filter of Object.values(get().selectedFilters)) {
      const greenSubFilter =
        filter.filters.find((subFilter) => subFilter.state === 'green' && !subFilter.propertyName);
      if (greenSubFilter) {
        return greenSubFilter.id;
      }
    }

    // If no green state found, return null
    return null;
  },

  resetSelectedFilters: () => {
    set({ selectedFilters: {}, prompterText: undefined });
    get().resetFilters();
  },

  // Snapshot the selected sub-filters (id + state) of one filter family, so a
  // sticky family (e.g. Catalog Tier) can be re-applied after resetSelectedFilters.
  getSelectedSubFilters: (title) => {
    const family = get().selectedFilters[title];
    if (!family) return [];
    return family.filters
      .filter((sf) => sf.state === 'green' || sf.state === 'red')
      .map((sf) => ({ id: sf.id, state: sf.state as 'green' | 'red' }));
  },

  // Re-apply a snapshot from getSelectedSubFilters onto the (possibly reset) tree
  // via the canonical setter, keeping selectedFilters and the filters tree in sync.
  reapplySubFilters: (title, entries) => {
    if (!entries.length) return;
    const filter = get().filters.find((f) => f.title === title);
    if (!filter) return;
    entries.forEach(({ id, state }) => {
      const subFilter = filter.filters.find((sf) => sf.id === id);
      if (subFilter) {
        get().addOrChangeSubFilterSelectedFilters(filter, subFilter, state, false);
      }
    });
  },

  constructFeaturesAndCheckIfNotEmpty: () => {
    // Angular wrapped this in ngZone.runOutsideAngular — in React we just run it.
    // Initialize the filterForApiCall
    const filterForApiCall = resetFiltersForApiCall();
    const selectedFilters = get().selectedFilters;

    // Iterate over the selectedFilters
    for (const key in selectedFilters) {
      const filter = selectedFilters[key];

      // Iterate over the SubFilters of the selected Filter
      for (const subFilter of filter.filters) {
        if (subFilter.propertyName) {
          if (subFilter.nonNumericMetadata) {
            const nonNumericKey = filter.title.toLowerCase();
            const f = {
              id: subFilter.id,
              name: subFilter.propertyName,
              state: subFilter.state === 'green',
            };
            if (filterForApiCall.nonNumericMetadata[nonNumericKey]) {
              filterForApiCall.nonNumericMetadata[nonNumericKey].push(f);
            } else {
              filterForApiCall.nonNumericMetadata[nonNumericKey] = [f];
            }
          } else {
            // This is metadata filter
            if (subFilter.state === 'green') {
              filterForApiCall.numericMetadata.push({
                propertyName: subFilter.propertyName,
                lower: subFilter.start,
                upper: subFilter.end,
                state: subFilter.state,
                id: subFilter.id,
              });
            } else if (subFilter.state === 'red') {
              filterForApiCall.numericMetadata.push({
                propertyName: subFilter.propertyName,
                lower: subFilter.min,
                upper: subFilter.start,
                lowerTwo: subFilter.end,
                upperTwo: subFilter.max,
                state: subFilter.state,
                id: subFilter.id,
              });
            }
          }
        } else {
          // This is normal filter
          if (subFilter.state === 'green') {
            filterForApiCall.features.push({
              id: subFilter.id,
              lower: subFilter.threshold,
              upper: 1000,
              state: subFilter.state,
            });
          } else if (subFilter.state === 'red') {
            filterForApiCall.features.push({
              id: subFilter.id,
              lower: 0,
              upper: subFilter.limit,
              state: subFilter.state,
            });
          }
        }
      }
    }

    const lyricsContains = get().lyricsContains;
    if (lyricsContains) {
      filterForApiCall.lyricsContains = lyricsContains;
    }

    const searchInArtist = get().artistSearchStr;
    if (searchInArtist) {
      filterForApiCall.searchInArtist = searchInArtist;
    }

    const multipleArtworksIds = get().multipleArtworksIds;
    if (multipleArtworksIds) {
      filterForApiCall.multipleArtworksIds = multipleArtworksIds;
    }

    const searchOnThisArtist = get().searchOnThisArtist;
    if (searchOnThisArtist) {
      filterForApiCall.searchOnThisArtist = searchOnThisArtist;
    }

    const narrative = get().narrative;
    if (narrative) {
      filterForApiCall.narrative = narrative;
    }

    set({ filterForApiCall });

    return filterForApiCall.features.length > 0
      || !!lyricsContains
      || !!searchInArtist
      || !!multipleArtworksIds
      || !!searchOnThisArtist
      || !!narrative
      || filterForApiCall.numericMetadata.length > 0
      || Object.keys(filterForApiCall.nonNumericMetadata).length > 0;
  },

  applyFilterFromUrl: (filterArray, primaryFilterId, lyricsContains, searchInArtist,
                       multipleArtworksIds, searchOnThisArtist, narrative) => {
    get().resetFilters();
    const currentFilters = get().filters;

    for (const filter of filterArray) {
      // Angular used non-null assertions here and crashed on unknown ids — but
      // inside a queryParams subscription Zone just logged the error and the
      // app survived. In React the same throw happens in a render effect and
      // unmounts MainContent via the router's ErrorBoundary, so unresolvable
      // ids (e.g. dynamic genre/sub-genre ids absent from the restored
      // originalFilters tree) are skipped instead.
      const correspondingFilter = findFilterBySubFilterId(currentFilters, filter.id);
      const correspondingSubFilter = correspondingFilter && findSubFilterById(correspondingFilter, filter.id);
      if (!correspondingFilter || !correspondingSubFilter) {
        console.warn(`applyFilterFromUrl: filter id ${filter.id} not found in the filter tree — skipping`);
        continue;
      }
      correspondingSubFilter.state = filter.state;

      const selectedFilter = get().selectedFilters[correspondingFilter.title];
      addOrUpdateSubFilter(get, selectedFilter, correspondingFilter, correspondingSubFilter, 'none');
    }

    if (primaryFilterId) {
      moveFilterToStartOfSelected(set, get, primaryFilterId);
    }

    if (lyricsContains) {
      get().setLyricsContains(lyricsContains);
    }

    if (searchInArtist) {
      get().setArtistSearchStr(searchInArtist);
    }

    if (multipleArtworksIds) {
      get().setMultipleArtworksIds(multipleArtworksIds);
    }

    if (searchOnThisArtist) {
      get().setSearchOnThisArtist(searchOnThisArtist);
    }

    if (narrative) {
      get().setNarrative(narrative);
    }

    get().setFilters(currentFilters);

    generatePrompterText(set, get);
  },

  convertFiltersToQueryString: () => {
    // Helper function to convert features and numericMetadata arrays to dictionary format
    const convertArrayToQueryString = (arr: (Feature | NumericMetadata | NonNumericMetadata)[]): string =>
      arr.map((item) => `id_${item.id}=${item.state}`).join('&');

    const convertNonNumericArrayToQueryString = (arr: NonNumericMetadata[]): string =>
      arr.map((item) => `id_${item.id}=${item.state ? 'green' : 'red'}`).join('&');

    const filterForApiCall = get().filterForApiCall;

    // Retrieve the ID from selectedArtwork
    const artworkId = get().selectedArtwork?.id;

    // Convert features and numericMetadata to query strings
    const featuresString = convertArrayToQueryString(filterForApiCall.features);
    const numericMetadataString = convertArrayToQueryString(filterForApiCall.numericMetadata);
    const nonNumericMetadataArrays = Object.values(filterForApiCall.nonNumericMetadata);
    const nonNumericMetadataStrings: string[] = [];
    for (const array of nonNumericMetadataArrays) {
      nonNumericMetadataStrings.push(convertNonNumericArrayToQueryString(array));
    }
    const nonNumericMetadataString = nonNumericMetadataStrings.join('&');

    // Build the query string using an array
    const queryStringParts: string[] = [];

    if (get().youtubeIdFromUrl) {
      queryStringParts.push(`youtubeId=${get().youtubeIdFromUrl}`);
    }

    if (artworkId !== undefined) {
      queryStringParts.push(`referenceSongIds=${artworkId}`);
      const sortByArray = get().sortByArray;
      const index = sortByArray.findIndex((sort) => sort.selected);
      queryStringParts.push(`sortByIndex=${index}`);
    }

    if (featuresString) queryStringParts.push(featuresString);
    if (numericMetadataString) queryStringParts.push(numericMetadataString);
    if (nonNumericMetadataString) queryStringParts.push(nonNumericMetadataString);
    if (filterForApiCall.primaryFilterId !== undefined) queryStringParts.push(`primaryFilterId=${filterForApiCall.primaryFilterId}`);
    // NOTE(port): percent-encode the free-text values. Angular emitted them raw,
    // so a search term containing '&', '=', '+', '%' or a space corrupted the
    // query string — values were truncated at the first '&'/'=' and '+'/space
    // round-tripped inconsistently between the browser-restored URL and the
    // rebuilt one (the back-button "+ turns into %xx" symptom). The read path
    // already decodes via URLSearchParams, so encoding here makes it symmetric.
    if (filterForApiCall.lyricsContains) queryStringParts.push(`lyricsContains=${encodeURIComponent(filterForApiCall.lyricsContains)}`);
    if (filterForApiCall.searchInArtist) queryStringParts.push(`searchInArtist=${encodeURIComponent(filterForApiCall.searchInArtist)}`);
    if (filterForApiCall.multipleArtworksIds) queryStringParts.push(`multipleArtworksIds=${filterForApiCall.multipleArtworksIds}`);
    if (filterForApiCall.searchOnThisArtist) queryStringParts.push(`searchOnThisArtist=${encodeURIComponent(filterForApiCall.searchOnThisArtist)}`);
    if (filterForApiCall.narrative) queryStringParts.push(`narrative=${encodeURIComponent(filterForApiCall.narrative)}`);

    // Concatenate the parts using & to form the final query string
    return queryStringParts.join('&');
  },

  areFiltersEmpty: () => Object.keys(get().selectedFilters).length === 0,

  resetParams: () => {
    set((state) => {
      const next = {
        ...state,
        lyricsContains: undefined,
        artistSearchStr: undefined,
        searchOnThisArtist: undefined,
        multipleArtworksIds: undefined,
        narrative: undefined,
        updateSearchPayloadSignal: generateRandomString(),
      };
      return {
        lyricsContains: next.lyricsContains,
        artistSearchStr: next.artistSearchStr,
        searchOnThisArtist: next.searchOnThisArtist,
        multipleArtworksIds: next.multipleArtworksIds,
        narrative: next.narrative,
        updateSearchPayloadSignal: next.updateSearchPayloadSignal,
        searchPayload: computeSearchPayload(next),
      };
    });
    schedulePrompterRegen();
  },
}));

type SetState = (
  partial: Partial<FiltersState> | ((state: FiltersState) => Partial<FiltersState>)
) => void;
type GetState = () => FiltersState;

/**
 * Port of the Angular constructor `effect()`:
 *
 *   effect(() => {
 *     const merged = this.searchPayloadSignal();   // tracks the computed
 *     clearTimeout(debounceTimer);
 *     debounceTimer = setTimeout(() => {
 *       if (merged.lyrics || merged.artist || merged.searchArtist || merged.narrative) {
 *         this.generatePrompterText();
 *       }
 *     }, 300);
 *   });
 *
 * Tracked signals (everything read inside `searchPayloadSignal`):
 *   1. _updateSearchPayloadSignal  (via updateSearchPayloadSignal getter)
 *   2. lyricsContains
 *   3. artistSearchStr
 *   4. searchOnThisArtist
 *   5. narrative
 *
 * Every store action that changes one of these five inputs must call
 * schedulePrompterRegen() (setLyricsContains, setArtistSearchStr,
 * setSearchOnThisArtist, setNarrative, setRandomUpdateSearchPayloadSignal,
 * resetParams — and transitively resetFilters / applyFilterFromUrl /
 * resetSelectedFilters). multipleArtworksIds is intentionally NOT a trigger.
 */
const schedulePrompterRegen = debounce(() => {
  const state = useFiltersStore.getState();
  if (state.lyricsContains || state.artistSearchStr || state.searchOnThisArtist || state.narrative) {
    generatePrompterText(useFiltersStore.setState, useFiltersStore.getState);
  }
}, 300);

// ---------------------------------------------------------------------------
// Private helpers (Angular private methods, ported as module-private functions)
// ---------------------------------------------------------------------------

function generatePrompterText(set: SetState, get: GetState): void {
  const lyricsContains = get().lyricsContains;
  const sortByText = getSortText(get);
  const artwork = get().selectedArtwork;
  const artistSearchStr = get().artistSearchStr;
  const searchOnThisArtist = get().searchOnThisArtist;
  const selectedFilters = get().selectedFilters;

  const narrative = get().narrative
    ? `<strong id="narrative" narrative="true" tabindex="0">${get().narrative}</strong>`
    : undefined;

  // Return if selectedFilters is empty
  if (!Object.keys(selectedFilters).length && !lyricsContains) {
    let title: string = 'Songs';

    // Helper to generate artist or artwork HTML
    const generateHtml = (id: string, name: string, additional?: string) =>
      `<strong id="${id}" artist-name="${name}">${name}${additional ? ` - ${additional}` : ''}</strong>`;

    if (artistSearchStr) {
      title += ` by ${generateHtml(`replaced-value-multiple-${artistSearchStr}`, artistSearchStr)}`;
    }

    if (artwork) {
      title += ` ${sortByText} ${generateHtml(`replaced-value-${artwork.id}`, artwork.artist, artwork.title)}`;
    } else if (searchOnThisArtist) {
      title += ` ${sortByText} ${generateHtml(`replaced-value-multiple-${searchOnThisArtist}`, searchOnThisArtist)}`;
    }

    if (narrative) {
      if (title !== 'Songs') {
        title += `, about ${narrative}`;
      } else {
        title += ` about ${narrative}`;
      }
    }

    // Only update if title has additional content
    set({ prompterText: title !== 'Songs' ? capitalizeFirstLetter(title) : undefined });
    return;
  }

  // Initialize the segments with placeholders
  const segmentsArray: { [title: string]: string[] } = {
    'Catalog Tier': [],
    'Popularity': [],
    'Decade': [],
    'Gender': [],
    'Genre': [],
    'Sub-genre': [],
    'Mood': [],
    'Sound': [],
    'Instruments': [],
    'Lyrical Theme': [],
    'Lyrical Mood': [],
    'Writing Style': [],
    'Harmony': [],
    'Melody': [],
  };

  // Populate segmentsArray based on selected filters
  for (const title in selectedFilters) {
    const filter = selectedFilters[title];
    const greenStateFilters: string[] = [];
    const redStateFilters: string[] = [];
    for (const subFilter of filter.filters) {
      if (subFilter.state === 'none') continue;

      let name = `<strong filter-family-name="${filter.title}" filter-state="${subFilter.state}" filter-id="${subFilter.id}" id="${subFilter.id}">` + subFilter.name + '</strong>';
      if (subFilter.state === 'red') {
        if (['Genre', 'Sub-genre'].includes(title)) {
          name = '<strong>non</strong> ' + name;
        } else if (['Writing Style', 'Instruments'].includes(title)) {
          name = '<strong>little to no</strong> ' + name;
        } else if (['Harmony', 'Melody'].includes(title)) {
          name = '<strong>are not</strong> ' + name;
        } else {
          name = '<strong>not</strong> ' + name;
        }
        redStateFilters.push(name);
      } else {
        greenStateFilters.push(name);
      }
    }
    segmentsArray[title] = [...greenStateFilters, ...redStateFilters];
  }

  const segmentsString: { [title: string]: string } = {};
  for (const title in segmentsArray) {
    if (segmentsArray[title].length) {
      segmentsString[title] = segmentsArray[title].join(', ');
    }
  }

  // Construct the final prompterText using the segmentsString
  let prompterText = '';

  if (segmentsString['Catalog Tier']) prompterText += segmentsString['Catalog Tier'] + ' catalog ';
  if (segmentsString['Popularity']) prompterText += segmentsString['Popularity'] + ' ';
  if (segmentsString['Decade']) prompterText += segmentsString['Decade'] + ' ';
  if (segmentsString['Gender']) prompterText += segmentsString['Gender'] + ' ';
  if (segmentsString['Genre']) prompterText += segmentsString['Genre'];
  if (segmentsString['Genre'] && segmentsString['Sub-genre']) prompterText += ', ';

  // Replace the ": " prefix inside the <strong> tag while keeping the tag and its attributes intact
  if (segmentsString['Sub-genre']) prompterText += segmentsString['Sub-genre'].replace(/(>)[^>]*?:\s*/g, '$1');

  prompterText += ' songs';

  const parts: string[] = [''];

  const addArtistOrArtwork = () => {
    if (artistSearchStr) {
      parts.push(`by <strong id="replaced-value-multiple-${artistSearchStr}" artist-name="${artistSearchStr}">${artistSearchStr}</strong>`);
    }
    if (artwork) {
      const artworkPart = `${sortByText} <strong id="replaced-value-${artwork.id}" artwork-id="${artwork.id}">${artwork.artist} - ${artwork.title}</strong>`;
      parts.push(artworkPart);
    } else if (searchOnThisArtist) {
      parts.push(`${sortByText} <strong id="replaced-value-multiple-${searchOnThisArtist}" artist-name="${searchOnThisArtist}">${searchOnThisArtist}</strong>`);
    }
  };

  addArtistOrArtwork();
  if (narrative) {
    parts.push(`about ${narrative}`);
  }

  // Join all parts with appropriate spacing and commas
  prompterText += parts.join(artistSearchStr || artwork || searchOnThisArtist ? ' ' : ', ');

  const moodSound = [segmentsString['Mood'], segmentsString['Sound']].filter((part) => part).join(', ');
  if (moodSound) prompterText += ' that are ' + moodSound;

  const segInstruments = segmentsString['Instruments'];

  if (segInstruments) {
    if (segInstruments?.startsWith('<strong>little to no</strong>')) {
      prompterText += ', featuring ' + segmentsString['Instruments'];
    } else {
      prompterText += ', featuring <strong>prevalent</strong> ' + segmentsString['Instruments'];
    }
  }

  const lyricalParts: string[] = [];

  if (segmentsString['Lyrical Theme']) {
    const themes = segmentsString['Lyrical Theme'].split(', ');

    const greenStateFilters = themes.filter((theme: string) => !theme.startsWith('<strong>not</strong> '));
    const redStateFilters = themes.filter((theme: string) => theme.startsWith('<strong>not</strong> '))
      .map((theme: string) => theme.replace('<strong>not</strong> ', ''));

    let lyricalThemeSegment = '';

    // First, handle green state filters
    if (greenStateFilters.length) {
      lyricalThemeSegment += 'about ' + greenStateFilters.join(', ');
    }

    // Then, handle red state filters
    if (redStateFilters.length) {
      if (lyricalThemeSegment) lyricalThemeSegment += ', ';
      lyricalThemeSegment += '<strong>not about</strong> ' + redStateFilters.join(', ');
    }

    if (lyricalThemeSegment) {
      lyricalParts.push(lyricalThemeSegment);
    }
  }
  if (segmentsString['Lyrical Mood'] || segmentsString['Writing Style']) {
    lyricalParts.push('that ');
  }

  if (segmentsString['Lyrical Mood']) lyricalParts.push('feel ' + segmentsString['Lyrical Mood']);

  if (lyricsContains) {
    const isPlural = lyricsContains.includes(' ');
    const text = isPlural
      ? ', that contain the words '
      : ', that contain the word ';
    prompterText += `${text}<strong id="lyricscontains" lyricscontains="true">${lyricsContains}</strong>`;
  }

  if (lyricalParts.length) {
    const addToText = prompterText === ' songs' ? ' with lyrics ' : ', with lyrics ';
    prompterText += addToText + lyricalParts.join(' ');
  }

  const remainingParts = ['Writing Style', 'Harmony', 'Melody'];
  for (let i = 0; i < remainingParts.length; i++) {
    const part = remainingParts[i];
    if (segmentsString[part]) {
      switch (i) {
        case 0:
          if (segmentsString[part].startsWith(`<strong>little to no`)) {
            prompterText += ', have ' + segmentsString[part];
          } else {
            prompterText += ', have <strong>plenty of</strong> ' + segmentsString[part];
          }
          break;
        case 1:
          prompterText += ', ' + segmentsString[part] + ' musically';
          break;
        case 2:
          if (segmentsString[part].startsWith(`<strong>are not`)) {
            prompterText += ', that ' + segmentsString[part];
          } else {
            prompterText += ', that are ' + segmentsString[part];
          }
          break;
      }
    }
  }

  prompterText = prompterText.replace(/\s+/g, ' ') // Replace multiple spaces with a single space
    .replace(/ , /g, ', ') // Replace " , " with ", "
    .replace(/that\s*,\s*have/g, 'that have'); // Replace "that , have" or "that, have" or "that ,have" with "that have"

  const totalSubFiltersCount = Object.values(selectedFilters)
    .reduce((total, filter) => total + filter.filters.length, 0);

  if ((segmentsString['Harmony'] || segmentsString['Melody']) &&
    !(segmentsString['Mood'] || segmentsString['Sound'] ||
      segmentsString['Lyrical Theme'] || segmentsString['Lyrical Mood'] || segmentsString['Writing Style'] ||
      lyricsContains)) {
    prompterText = prompterText.replace(/, (have|are)(.*)$/, (_match, p1: string, p2: string) => ` that ${p1}${p2}`);
  }

  if (totalSubFiltersCount > 2) {
    if (segmentsString['Lyrical Theme'] || segmentsString['Lyrical Mood'] || segmentsString['Writing Style']
      || segmentsString['Harmony'] || segmentsString['Melody']) {
      prompterText = prompterText.replace(/,([^,]*)$/, ', and$1'); // Replace last comma with ,and
    }
  }

  set({ prompterText: capitalizeFirstLetter(prompterText.trim()) });
}

function capitalizeFirstLetter(str: string): string {
  if (!str) return ''; // Return an empty string if str is empty or not provided
  return str.charAt(0).toUpperCase() + str.slice(1);
}

function moveFilterToStartOfSelected(set: SetState, get: GetState, filterId: number): void {
  const selectedFilters = get().selectedFilters;
  let targetFilterTitle: string | null = null;

  // Find the title of the filter with the targetId
  for (const title in selectedFilters) {
    if (selectedFilters[title].filters.some((sf) => sf.id === filterId)) {
      targetFilterTitle = title;
      break;
    }
  }

  // If we found the target filter, move it to the start
  if (targetFilterTitle) {
    const targetFilter = selectedFilters[targetFilterTitle];
    // Remove the target filter from selectedFilters
    delete selectedFilters[targetFilterTitle];
    // Create a new object with the target filter at the start
    set({
      selectedFilters: {
        [targetFilterTitle]: targetFilter,
        ...selectedFilters,
      },
    });
  }
}

function isSingleSelection(filterTitle: string): boolean {
  // return filterTitle === 'Genre' || filterTitle === 'Decade' || filterTitle === 'Gender';
  return filterTitle === 'Genre';
}

function ensureSingleSelectionActive(get: GetState, filter: Filter, activeSubFilter: SubFilter): void {
  filter.filters.forEach((sf) => {
    if (sf.id !== activeSubFilter.id && sf.state !== 'none') {
      sf.state = 'none';
      filter.totalActiveFilters -= 1; // decrease the active count for each de-selected sub-filter
    }
  });

  const selectedFilter = get().selectedFilters[filter.title];
  if (selectedFilter) {
    selectedFilter.filters = []; // Clear any existing filters in the selectedFilters for 'Genre'
  }
}

function removeSubFilterIfNecessary(get: GetState, selectedFilter: Filter, filter: Filter, subFilter: SubFilter): void {
  const subFilterIndex = selectedFilter.filters.findIndex((sf) => sf.id === subFilter.id);
  if (subFilterIndex !== -1) {
    selectedFilter.filters.splice(subFilterIndex, 1);
    filter.totalActiveFilters -= 1;
    if (selectedFilter.filters.length === 0) {
      delete get().selectedFilters[filter.title];
    }
  }
}

function addOrUpdateSubFilter(get: GetState, selectedFilter: Filter | undefined, filter: Filter,
                              subFilter: SubFilter, prevState: string): void {
  if (selectedFilter) {
    const subFilterIndex = selectedFilter.filters.findIndex((sf) => sf.id === subFilter.id);
    if (subFilterIndex !== -1) {
      selectedFilter.filters[subFilterIndex] = subFilter;
    } else {
      selectedFilter.filters.push(subFilter);
    }
  } else {
    // NOTE: the Filter shell is deep-cloned, but `filters` then holds the LIVE
    // SubFilter reference from the main tree (identity sharing is intentional).
    const filterCopy: Filter = JSON.parse(JSON.stringify(filter));
    filterCopy.filters = [subFilter];
    get().selectedFilters[filter.title] = filterCopy;
  }

  if (prevState === 'none') {
    filter.totalActiveFilters += 1;
  }
}

function checkFilterOverload(get: GetState): void {
  const dontShow = localStorage.getItem('dont_show_filter_overload');
  if (dontShow) {
    return;
  }
  let totalFilters = 0;

  for (const filter of Object.values(get().selectedFilters)) {
    totalFilters += filter.filters.length;
    if (totalFilters > 5) {
      useModalStore.getState().open(ModalType.FILTER_OVERLOAD);
      return;
    }
  }
}

function handleGenreSubgenreLogic(get: GetState, filter: Filter, subFilter: SubFilter): void {
  const primaryGenreTitle = 'Genre';
  const subGenreTitle = 'Sub-genre';

  // Detect the current and opposite filter titles
  const currentFilterTitle = filter.title;
  const oppositeFilterTitle = currentFilterTitle === primaryGenreTitle ? subGenreTitle : primaryGenreTitle;

  // Get the primary genre name prefix
  const primaryGenrePrefix = subFilter.name.split(':')[0];

  // Check the opposite filter title from the selectedFilters
  const selectedFilters = get().selectedFilters;
  const oppositeSelectedFilter = selectedFilters[oppositeFilterTitle];
  if (oppositeSelectedFilter) {
    const matchingFilters = oppositeSelectedFilter.filters.filter((sf) => sf.name.includes(primaryGenrePrefix));

    if (matchingFilters && matchingFilters.length > 0) {
      // 1. Remove the primary genres from selectedFilters that match the criteria
      selectedFilters[oppositeFilterTitle].filters =
        selectedFilters[oppositeFilterTitle].filters.filter((f) => !matchingFilters.includes(f));

      if (selectedFilters[oppositeFilterTitle].filters.length === 0) {
        delete selectedFilters[oppositeFilterTitle];
      }

      // 2. Update the filter itself in the _filters object with the state of none for each matching filter
      const allFilters = get().filters;
      const targetFilter = allFilters.find((f) => f.title === oppositeFilterTitle);
      if (targetFilter) {
        matchingFilters.forEach((matchingFilter) => {
          const targetSubFilter = targetFilter.filters.find((sf) => sf.name === matchingFilter.name);
          if (targetSubFilter) {
            targetSubFilter.state = 'none';
          }
        });
        // 3. Update totalActiveFilters in the Genre
        targetFilter.totalActiveFilters -= matchingFilters.length;
      }
    }
  }
}

// Helper function to find the main filter by a sub-filter's ID
function findFilterBySubFilterId(filters: Filter[], id: number): Filter | undefined {
  return filters.find((filter) => filter.filters.some((sf) => sf.id === id));
}

// Helper function to find the sub-filter by its ID within a given filter
function findSubFilterById(filter: Filter, id: number): SubFilter | undefined {
  return filter.filters.find((sf) => sf.id === id);
}

// Helper function to find the main filter by a sub-filter's propertyName
export function findFilterByPropertyName(filters: Filter[], propertyName: string): Filter | undefined {
  return filters.find((filter) => filter.filters.some((sf) => sf.propertyName === propertyName));
}

// Helper function to find the sub-filter by its propertyName within a given filter
export function findSubFilterByPropertyName(filter: Filter, propertyName: string): SubFilter | undefined {
  return filter.filters.find((sf) => sf.propertyName === propertyName);
}

function getSortText(get: GetState): string {
  const sortByArray = get().sortByArray;
  const selected = sortByArray.find((sortBy) => sortBy.selected)?.name;
  switch (selected) {
    case 'Weighted Similarity':
      return 'similar to';
    case 'Production & Sound':
      return 'that sound like';
    case 'Theme & Mood':
      return 'that are similar in lyrical theme or mood to';
    case 'Writing Style':
      return 'that are similar in lyrical writing style to';
    case 'Music & Arrangement':
      return 'that are musically similar to';
  }
  return 'similar to';
}
